import { addSwipe } from '../../utils/swipe';

const INIT_FLAG = 'stgbCatCarouselInit';

// Must match the desktop breakpoint in _save.scss (em(1100) = 68.75em).
const DESKTOP_QUERY = '(min-width: 68.75em)';

const initCarousel = (root: HTMLElement) => {
    if (root.dataset[INIT_FLAG] === 'true') return;

    const track = root.querySelector<HTMLElement>('.stgb-cat-carousel-track');
    const viewport = root.querySelector<HTMLElement>('.stgb-cat-carousel-viewport');
    const prevBtn = root.querySelector<HTMLButtonElement>('.stgb-cat-carousel-arrow--prev');
    const nextBtn = root.querySelector<HTMLButtonElement>('.stgb-cat-carousel-arrow--next');
    const status = root.querySelector<HTMLElement>('.stgb-cat-carousel-status');

    if (!track || !viewport || track.children.length === 0) return;

    root.dataset[INIT_FLAG] = 'true';

    // loop === true: infinite repeat in BOTH directions (cards always available).
    // loop === false: finite, clamp + disable arrows at the ends.
    const infinite = root.dataset.loop === 'true';
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const statusTemplate = (status && status.dataset.template) || '%1$s';

    // The original, un-cloned set — cloned from to keep both sides stocked.
    const originals = Array.from(track.children) as HTMLElement[];
    let active = 0;

    const items = (): HTMLElement[] => Array.from(track.children) as HTMLElement[];

    const measureAdvance = (): number => {
        const first = track.children[0] as HTMLElement | undefined;
        if (!first) return 1;
        const width = first.getBoundingClientRect().width;
        const cs = getComputedStyle(track);
        const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
        return Math.max(1, width + gap);
    };

    const labelOf = (el: Element): string => {
        const l =
            el.querySelector('.stgb-cat-carousel-label-text') ||
            el.querySelector('.stgb-cat-carousel-label');
        return l ? (l.textContent || '').trim() : '';
    };

    const apply = (announce: boolean) => {
        const its = items();
        track.style.setProperty('--active', String(active));

        // The active card is dead-centre, so only about half a viewport of cards
        // sits to its right. Expose the active card + that window; hide the faded
        // left cards and the off-screen/cloned cards from AT and the tab order.
        const visibleRight = Math.ceil(viewport.clientWidth / 2 / measureAdvance()) + 1;

        its.forEach((item, i) => {
            item.classList.toggle('is-active', i === active);
            item.classList.toggle('is-past', i < active);

            const hidden = i < active || i > active + visibleRight;
            item.setAttribute('aria-hidden', hidden ? 'true' : 'false');

            const link = item.querySelector<HTMLElement>('a, button');
            if (link) link.tabIndex = hidden ? -1 : 0;
        });

        if (!infinite) {
            if (prevBtn) prevBtn.disabled = active === 0;
            if (nextBtn) nextBtn.disabled = active === its.length - 1;
        }

        if (announce && status && its[active]) {
            const name = labelOf(its[active]);
            status.textContent = statusTemplate.replace('%1$s', () => name);
        }
    };

    const cloneSet = (): HTMLElement[] =>
        originals.map((n) => n.cloneNode(true) as HTMLElement);

    // Run a DOM mutation that would otherwise animate the transform, with the
    // transition suppressed so the active card doesn't visibly jump.
    const silently = (fn: () => void) => {
        const prev = track.style.transition;
        track.style.transition = 'none';
        fn();
        void track.offsetWidth; // force reflow
        track.style.transition = prev;
    };

    // Insert a whole set of clones at the front; shift active + --active to keep
    // the active card in the exact same place.
    const prependSet = () => {
        silently(() => {
            const first = track.firstChild;
            const clones = cloneSet();
            for (const c of clones) track.insertBefore(c, first);
            active += clones.length;
            track.style.setProperty('--active', String(active));
        });
    };

    // Append a whole set of clones at the end (doesn't move the active card).
    const appendSet = () => {
        for (const c of cloneSet()) track.appendChild(c);
    };

    // Move the first card to the end (keeps the active card centred).
    const shiftFirstToEnd = () => {
        silently(() => {
            track.appendChild(track.firstElementChild as HTMLElement);
            active -= 1;
            track.style.setProperty('--active', String(active));
        });
    };

    // Move the last card to the front (keeps the active card centred).
    const shiftLastToStart = () => {
        silently(() => {
            track.insertBefore(track.lastElementChild as HTMLElement, track.firstChild);
            active += 1;
            track.style.setProperty('--active', String(active));
        });
    };

    // Keep at least `perSide` cards on BOTH sides of the active card, and move
    // any excess from one far end to the other so the pool stays bounded. All of
    // this happens off-screen with the transition suppressed, so it's invisible.
    const ensureBalanced = () => {
        if (!infinite) return;
        const advance = measureAdvance();
        const perSide = Math.ceil(window.innerWidth / 2 / advance) + 3;
        const total = originals.length;
        let guard;

        guard = 0;
        while (active < perSide && guard++ < 100) prependSet();

        guard = 0;
        while (track.children.length - 1 - active < perSide && guard++ < 100) appendSet();

        guard = 0;
        while (active > perSide + total && guard++ < 100) shiftFirstToEnd();

        guard = 0;
        while (track.children.length - 1 - active > perSide + total && guard++ < 100) {
            shiftLastToStart();
        }
    };

    const step = (dir: -1 | 1) => {
        if (infinite) {
            ensureBalanced(); // guarantees a card exists in either direction
            active += dir;
            active = Math.max(0, Math.min(active, track.children.length - 1));
        } else {
            active = Math.max(0, Math.min(active + dir, track.children.length - 1));
        }
        apply(true);
    };

    nextBtn?.addEventListener('click', () => step(1));
    prevBtn?.addEventListener('click', () => step(-1));

    // Touch swipe (mobile): mirror the arrow controls.
    addSwipe(root, { onNext: () => step(1), onPrev: () => step(-1) });

    root.addEventListener('keydown', (event) => {
        // Never hijack modified arrow keys (Shift+Arrow selection, OS shortcuts).
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key === 'ArrowRight') {
            step(1);
        } else if (event.key === 'ArrowLeft') {
            step(-1);
        }
    });

    // Re-balance + re-evaluate hidden state when the layout width changes (the
    // required buffer depends on the viewport width and card size).
    desktop.addEventListener('change', () => { ensureBalanced(); apply(false); });
    let resizeRaf = 0;
    window.addEventListener('resize', () => {
        if (resizeRaf) return;
        resizeRaf = window.requestAnimationFrame(() => {
            resizeRaf = 0;
            ensureBalanced();
            apply(false);
        });
    });

    // Suppress the fade during initial setup so the left/off-screen cards are
    // hidden immediately on load instead of fading out; navigation still fades.
    root.classList.add('stgb-cc-no-anim');
    ensureBalanced();
    apply(false);
    void root.offsetWidth; // commit the hidden state before re-enabling transitions
    root.classList.remove('stgb-cc-no-anim');
};

const initAll = () => {
    // The front-end script is also enqueued in the editor; skip it there so it
    // never fights with the React-rendered editor preview.
    if (document.body.classList.contains('wp-admin')) return;
    document.querySelectorAll<HTMLElement>('.stgb-cat-carousel').forEach(initCarousel);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
} else {
    initAll();
}

// Mark this side-effect file as a module (module-scoped top-level constants).
export {};
